'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      question: 'What is your typical response time?',
      answer: 'We respond to all email inquiries within 2 business hours and return phone calls the same business day. For urgent fleet liquidation needs, our emergency line is available 24/7 with immediate response.',
      category: 'Response Times'
    },
    {
      question: 'Do you offer on-site fleet evaluations?',
      answer: 'Yes, we provide complimentary on-site evaluations for fleets of 5 or more vehicles. Our specialists will visit your location, inspect each vehicle, and provide detailed valuation reports within 24-48 hours.',
      category: 'Services'
    },
    {
      question: 'What areas do you serve?',
      answer: 'We serve all 50 states with three regional offices strategically located in Charlotte NC, Indianapolis IN, and Phoenix AZ. Our team can travel to your location regardless of where your fleet is based.',
      category: 'Coverage'
    },
    {
      question: 'How quickly can you complete a fleet purchase?',
      answer: 'Standard transactions typically close within 7-10 business days. For urgent liquidations, we can expedite the process and complete purchases in as little as 48-72 hours with our emergency services team.',
      category: 'Timeline'
    },
    {
      question: 'What types of commercial vehicles do you purchase?',
      answer: 'We purchase all types of commercial vehicles including box trucks, cargo vans, delivery vehicles, semi-trucks, trailers, service vehicles, and specialty equipment. No fleet is too large or too small.',
      category: 'Vehicle Types'
    },
    {
      question: 'Do I need to prepare vehicles before evaluation?',
      answer: 'No preparation is necessary. We evaluate vehicles in their current condition. However, having maintenance records, title documents, and vehicle history reports available can help expedite the valuation process.',
      category: 'Preparation'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4 mx-auto"></div>
            <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
            <div className="space-y-3 mt-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-card">
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-headline text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground">
            Quick answers to common questions about contacting us and our services
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-background rounded-lg shadow-brand overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-200"
              >
                <div className="flex-1 pr-4">
                  <span className="text-xs font-semibold text-primary mb-1 block">
                    {faq.category}
                  </span>
                  <span className="text-base font-semibold text-foreground">
                    {faq.question}
                  </span>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  variant="outline"
                  className="text-primary flex-shrink-0"
                />
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Still have questions? We're here to help.
          </p>
          <a
            href="tel:1-800-353-3899"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-cta text-cta-foreground font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-0.5 shadow-brand"
          >
            <Icon name="PhoneIcon" size={20} variant="solid" />
            <span>Call Us Now</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;